import { createResource, createSignal, For, Show } from "solid-js";
import type {
  DeviceTemplate,
  ExtensionInfo,
  ExtensionState,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { t, tOr } from "../i18n.ts";
import { capabilityId } from "../model.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { PageHeader, Skeleton } from "../components/ui.tsx";

const STATE_CHIP: Record<ExtensionState, string> = {
  installed: "",
  configured: "acc",
  ready: "ok",
  stopped: "stale",
  failed: "crit",
};

/**
 * Extensions (prototype screen "Rozszerzenia"): what each installed extension contributes — capabilities, device
 * templates, integrations — with its state and the permissions it was granted.
 */
export function ExtensionsPage() {
  const [data] = createResource(() => api.extensions());
  const [templates] = createResource(() => api.catalog.deviceTemplates());
  const [selected, setSelected] = createSignal<string>();
  const current = () =>
    data()?.extensions.find((e) => e.id === selected()) ??
    data()?.extensions[0];

  return (
    <>
      <PageHeader
        eyebrow={t("nav.configuration")}
        title={t("extensions.title")}
        sub={t("extensions.intro")}
      />
      <Show when={!data.error} fallback={<ErrorBox error={data.error} />}>
        <Show
          when={data()}
          fallback={
            <section class="card">
              <Skeleton />
            </section>
          }
        >
          <div class="grid g-2 g-2s">
            <section class="card">
              <table class="tbl">
                <thead>
                  <tr>
                    <th>{t("extensions.extension")}</th>
                    <th>{t("extensions.state")}</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={data()!.extensions}>
                    {(e) => (
                      <tr
                        class={`click ${e.id === current()?.id ? "sel" : ""}`}
                        onClick={() => setSelected(e.id)}
                      >
                        <td>
                          <b style={{ "font-weight": 500 }}>
                            {tOr(`extension.${e.id}`, e.id)}
                          </b>
                          <div class="hint">
                            <span class="mono">{e.id}</span> · {e.version} ·{" "}
                            {t(`tier.${e.tier}`)}
                          </div>
                        </td>
                        <td>
                          <span class={`chip ${STATE_CHIP[e.state]}`}>
                            {t(`extensionState.${e.state}`)}
                          </span>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </section>
            <Show when={current()} keyed>
              {(e) => (
                <ExtensionDetail
                  extension={e}
                  templates={(templates() ?? []).filter((tpl) =>
                    e.contributes.templates.includes(tpl.id),
                  )}
                />
              )}
            </Show>
          </div>
        </Show>
      </Show>
    </>
  );
}

function ExtensionDetail(props: {
  extension: ExtensionInfo;
  templates: DeviceTemplate[];
}) {
  const e = () => props.extension;
  const c = () => props.extension.contributes;
  return (
    <section class="card">
      <div class="card-h">
        <Icon name="folder" />
        <h2>{tOr(`extension.${e().id}`, e().id)}</h2>
        <span class={`chip ${STATE_CHIP[e().state]}`}>
          {t(`extensionState.${e().state}`)}
        </span>
        <span class="meta">
          {e().version} · {t(`tier.${e().tier}`)}
        </span>
      </div>
      <div class="card-b stack">
        <Show when={e().errorCode}>
          {(code) => (
            <div class="err">
              <b>{tOr(`errors.${code()}`, code())}</b>
              <span class="mono">{code()}</span>
            </div>
          )}
        </Show>
        <p class="muted" style={{ margin: 0 }}>
          {tOr(`extensionInfo.${e().id}`, t("extensions.noDescription"))}
        </p>
        <div class="axes">
          <div class="axis">
            <span class="eyebrow">
              {t("extensions.contributes.capabilities")}
            </span>
            <span class="num">{c().capabilities.length}</span>
          </div>
          <div class="axis">
            <span class="eyebrow">{t("extensions.contributes.templates")}</span>
            <span class="num">{c().templates.length}</span>
          </div>
          <div class="axis">
            <span class="eyebrow">
              {t("extensions.contributes.integration")}
            </span>
            <span>
              {c().adapters.length > 0
                ? c()
                    .adapters.map((a) => tOr(`adapter.${a}`, a))
                    .join(", ")
                : "–"}
            </span>
          </div>
        </div>
        <div>
          <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
            {t("extensions.permissions")}
          </div>
          <div class="q">
            <Show
              when={e().permissions.length > 0}
              fallback={
                <span class="hint">{t("extensions.noPermissions")}</span>
              }
            >
              <For each={e().permissions}>
                {(p) => <span class="chip">{tOr(`permission.${p}`, p)}</span>}
              </For>
            </Show>
          </div>
        </div>
        <Show when={c().capabilities.length > 0}>
          <div>
            <div class="eyebrow" style={{ "margin-bottom": "6px" }}>
              {t("extensions.contributes.capabilities")}
            </div>
            <div class="q">
              <For each={c().capabilities}>
                {(ref) => (
                  <span class="chip acc">
                    {tOr(`capability.${capabilityId(ref)}`, ref)}
                  </span>
                )}
              </For>
            </div>
          </div>
        </Show>
      </div>
      <Show when={props.templates.length > 0}>
        <div class="scrollx">
          <table class="tbl">
            <thead>
              <tr>
                <th>{t("catalog.model")}</th>
                <th>{t("catalog.channels")}</th>
              </tr>
            </thead>
            <tbody>
              <For each={props.templates}>
                {(tpl) => (
                  <tr>
                    <td>
                      {tpl.vendor} · {tpl.model}
                      <div class="hint mono">{tpl.id}</div>
                    </td>
                    <td class="hint">
                      {[
                        ...new Set(
                          tpl.channels.map((c) =>
                            tOr(
                              `capability.${capabilityId(c.capability)}`,
                              c.capability,
                            ),
                          ),
                        ),
                      ].join(", ")}
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </section>
  );
}
